import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2.ts';
import type { DealInsightsBidRateWinRateApi } from '@openx/types/dealInsights.ts';
import { toPercentString } from '@openx/utils/lib/toPercentString.ts';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { NameType, Payload, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { TooltipMetrics } from '../../shared/TooltipMetrics.tsx';

export type RateComparsionTooltipPayload = Omit<Payload<ValueType, NameType>, 'payload'> & {
  payload?: Partial<DealInsightsBidRateWinRateApi>;
}; // removes the original `payload: any` and replaces it with our typed version

type RateComparsionTooltipProps = {
  label?: string;
  payload?: RateComparsionTooltipPayload[];
};

export const RateComparsionTooltip = ({ label, payload }: RateComparsionTooltipProps) => {
  const { t } = useTranslation();

  const winRatePoint: RateComparsionTooltipPayload | undefined = payload?.find(point => point.dataKey === 'win_rate');
  const bidRatePoint: RateComparsionTooltipPayload | undefined = payload?.find(point => point.dataKey === 'bid_rate');

  if (!label || !payload?.length || !winRatePoint || winRatePoint.value == null) {
    return null;
  }

  const impressions = winRatePoint.payload?.impressions;
  const vcr = winRatePoint.payload?.vcr;
  const viewability = winRatePoint.payload?.viewability;

  return (
    <Paper
      elevation={0}
      sx={{ py: 1, px: 2, border: `1px solid ${palette.divider}` }}
      data-test="rate-comparsion-tooltip"
    >
      <Typography data-test="rate-comparsion-tooltip-date" variant="h3">
        {format(parseISO(label), 'MMM d, yyyy')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          <Box sx={{ width: 12, height: 12, backgroundColor: palette.charts.blue, borderRadius: '50%' }} />
          <Typography data-test="rate-comparsion-tooltip-win-rate" sx={{ display: 'flex' }}>
            {t('Win Rate')}{' '}
            <Typography
              variant="body1Bold"
              component={'span'}
              sx={{
                marginLeft: 1,
                display: 'flex',
              }}
            >
              {toPercentString(winRatePoint.value?.toString(), { decimalPlaces: 2 })}
            </Typography>
          </Typography>
        </Box>

        {bidRatePoint?.value !== undefined && bidRatePoint?.value !== null && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
            <Box sx={{ width: 12, height: 12, backgroundColor: palette.charts.lightBlue, borderRadius: '50%' }} />
            <Typography data-test="rate-comparsion-tooltip-bid-rate" sx={{ display: 'flex' }}>
              {t('Bid Rate')}{' '}
              <Typography
                variant="body1Bold"
                component={'span'}
                sx={{
                  marginLeft: 1,
                  display: 'flex',
                }}
              >
                {toPercentString(bidRatePoint.value?.toString(), { decimalPlaces: 2 })}
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>

      <TooltipMetrics
        dataTestPrefix="rate-comparsion-tooltip"
        impressions={impressions}
        viewability={viewability}
        vcr={vcr}
      />
    </Paper>
  );
};
